import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useProviderStore } from "@/stores/providerStore";
import { api } from "@/lib/api";
import { User, Mail, LogOut, Trash2, AlertTriangle } from "lucide-react";

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { tokens, disconnectProvider } = useProviderStore();
  const [error, setError] = useState("");

  const handleDeleteAccount = async () => {
    if (!confirm("Delete your account? This cannot be undone.")) return;
    try {
      await api.deleteAccount();
      await logout();
      navigate("/login");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete account");
    }
  };

  const providers = [
    { key: "gmail" as const, label: "Gmail", connected: !!tokens.gmail },
    { key: "outlook" as const, label: "Outlook", connected: !!tokens.outlook },
  ];

  return (
    <div className="max-w-xl space-y-5">
      {/* Account */}
      <div className="rounded-xl border border-border-default bg-surface-0 p-5 shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="p-1.5 rounded-lg bg-accent-50 text-accent-600 dark:bg-accent-500/10 dark:text-accent-400">
            <User size={16} />
          </div>
          <h3 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">Account</h3>
        </div>
        <div className="flex items-center gap-2 text-sm text-text-secondary dark:text-dark-text-secondary">
          <Mail size={14} className="text-text-muted" />
          {user?.email}
        </div>
      </div>

      {/* Providers */}
      <div className="rounded-xl border border-border-default bg-surface-0 p-5 shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="p-1.5 rounded-lg bg-info-50 text-info-600 dark:bg-info-500/10 dark:text-info-400">
            <Mail size={16} />
          </div>
          <h3 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">Providers</h3>
        </div>
        <div className="space-y-2">
          {providers.map((p) => (
            <div key={p.key} className="flex items-center justify-between rounded-lg bg-surface-1 dark:bg-dark-surface-2 p-3">
              <div className="flex items-center gap-2.5">
                <span className="text-sm font-medium text-text-primary dark:text-dark-text-primary capitalize">{p.label}</span>
                {p.connected ? (
                  <span className="badge bg-success-100 text-success-600 dark:bg-success-500/10 dark:text-success-400">Connected</span>
                ) : (
                  <span className="badge bg-surface-2 text-text-muted dark:bg-dark-surface-3 dark:text-dark-text-muted">Not connected</span>
                )}
              </div>
              {p.connected ? (
                <button
                  onClick={() => disconnectProvider(p.key)}
                  className="rounded-md border border-border-strong px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-2 dark:border-dark-border-strong dark:text-dark-text-secondary dark:hover:bg-dark-surface-2"
                >
                  Disconnect
                </button>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="rounded-xl border border-border-default bg-surface-0 p-5 shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="p-1.5 rounded-lg bg-danger-50 text-danger-600 dark:bg-danger-500/10 dark:text-danger-400">
            <AlertTriangle size={16} />
          </div>
          <h3 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">Danger Zone</h3>
        </div>
        <div className="space-y-2">
          <button
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="w-full flex items-center justify-center gap-2 rounded-lg border border-border-strong py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-2 dark:border-dark-border-strong dark:text-dark-text-secondary dark:hover:bg-dark-surface-2"
          >
            <LogOut size={16} />
            Log out
          </button>
          <button
            onClick={handleDeleteAccount}
            className="w-full flex items-center justify-center gap-2 rounded-lg border border-danger-100 bg-danger-50 py-2.5 text-sm font-medium text-danger transition-colors hover:bg-danger-100 dark:border-danger-500/20 dark:bg-danger-500/10 dark:text-danger-400 dark:hover:bg-danger-500/20"
          >
            <Trash2 size={16} />
            Delete account
          </button>
        </div>
        {error && <p className="mt-3 text-sm text-danger">{error}</p>}
      </div>
    </div>
  );
}
